import type { Property, Guest, Reservation } from '@/types';
import i18n from '@/i18n';

const endpoint        = __APPWRITE_ENDPOINT__       as string;
const projectId       = __APPWRITE_PROJECT__        as string;
const portalFunctionId = __GUEST_PORTAL_FUNCTION_ID__ as string;
const siteStatusFunctionId = __SITE_STATUS_FUNCTION_ID__ as string;

// ── Appwrite Functions REST helper ─────────────────────────────────────────────
// Uses native fetch directly to avoid the node-appwrite server SDK's
// node-fetch-native-with-agent polyfill, which breaks on iOS Safari (ATS/TLS).

async function executeFunction(
  functionId: string,
  body: string,
  timeoutMs = 30_000
): Promise<string> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${endpoint}/functions/${functionId}/executions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-appwrite-project': projectId,
      },
      body: JSON.stringify({
        body,
        async: false,
        path: '/',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    throw err;
  }
  clearTimeout(timer);

  if (!response.ok) {
    throw new Error(i18n.t('error.serverError'));
  }

  const execution = await response.json() as { responseBody?: string; response?: string };
  return execution.responseBody ?? execution.response ?? '{}';
}

// ── GuestPortal function caller ────────────────────────────────────────────────

async function callPortal<T = unknown>(
  action: string,
  bookingId: string,
  token: string | null,
  data?: Record<string, unknown>,
  timeoutMs = 30_000
): Promise<T> {
  if (!token) throw new Error(i18n.t('error.invalidToken'));

  const body = JSON.stringify({ bookingId, token, action, data: data ?? {}, lang: i18n.language });
  const raw = await executeFunction(portalFunctionId, body, timeoutMs);

  let result: { success: boolean; error?: string } & Record<string, unknown>;
  try {
    result = JSON.parse(raw);
  } catch {
    throw new Error(i18n.t('error.invalidResponse'));
  }

  if (!result.success) {
    throw new Error(result.error || i18n.t('error.serverError'));
  }

  return result as T;
}

// ── Public API ─────────────────────────────────────────────────────────────────

export function checkTokenFormat(token: string | null): void {
  if (!token || token.length < 32) {
    throw new Error(i18n.t('error.invalidToken'));
  }
}

export async function getReservation(
  reservationId: string,
  token: string | null
): Promise<{ reservation: Reservation; property: Property; guests: Guest[]; tokenUsed: boolean }> {
  const result = await callPortal<{
    reservation: Reservation;
    property: Property;
    guests: Guest[];
    tokenUsed?: boolean;
  }>('validate', reservationId, token);
  return {
    reservation: result.reservation,
    property: result.property,
    guests: result.guests,
    tokenUsed: result.tokenUsed === true,
  };
}

export async function getSiteStatus(): Promise<{ maintenanceEnabled: boolean; maintenanceMessage: string }> {
  try {
    const raw = await executeFunction(siteStatusFunctionId, '{}');
    const result = JSON.parse(raw);
    const m = result.guest?.maintenance ?? {};
    return {
      maintenanceEnabled: m.enabled ?? false,
      maintenanceMessage: m.message ?? '',
    };
  } catch (err) {
    console.error('[SiteStatus] fetch failed:', err);
    return { maintenanceEnabled: false, maintenanceMessage: '' };
  }
}

export async function saveGuest(
  reservationId: string,
  token: string | null,
  guestData: Omit<Guest, 'id' | 'uploaded'>,
  guestOrder: number
): Promise<void> {
  await callPortal('saveGuest', reservationId, token, { guestData, guestOrder });
}

export async function submitConsent(
  reservationId: string,
  token: string | null,
  confirmationCode: string,
  guestEmail: string
): Promise<void> {
  await callPortal('submitConsent', reservationId, token, { confirmationCode, guestEmail });
}

export async function updatePlannedArrival(
  reservationId: string,
  token: string | null,
  plannedArrivalTime: string
): Promise<void> {
  await callPortal('updatePlannedArrival', reservationId, token, { plannedArrivalTime });
}

// Compress image files to reduce upload size. PDFs are passed through unchanged.
async function compressImage(file: File, maxDimension = 1800, quality = 0.82): Promise<File> {
  if (file.type === 'application/pdf' || !file.type.startsWith('image/')) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width >= height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// Convert a File to a base64 string (data URL prefix stripped).
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Upload a guest document via the GuestPortal function.
// The function validates the magic-link token before accepting the file,
// so no unauthenticated user can write to the storage bucket directly.
export async function uploadGuestFile(
  file: File,
  reservationId: string,
  token: string | null,
  fileName: string,
  guestOrder: number,
): Promise<{ fileId: string; fileName: string; fileSize: number }> {
  const compressed = await compressImage(file);
  const base64 = await fileToBase64(compressed);

  const result = await callPortal<{ success: true; fileId: string; fileName: string; fileSize: number }>(
    'uploadGuestFile',
    reservationId,
    token,
    { fileData: base64, mimeType: compressed.type, fileName, guestOrder },
    90_000, // larger timeout for file upload
  );

  return { fileId: result.fileId, fileName: result.fileName, fileSize: result.fileSize };
}

export function generateConfirmationCode(reservationId: string): string {
  const hash = reservationId
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const part1 =
    letters[hash % letters.length] +
    letters[(hash >> 3) % letters.length] +
    letters[(hash >> 6) % letters.length];
  const part2 = String((hash * 7 + 1234) % 9000 + 1000);
  return `${part1}-${part2}`;
}
